import {
  NEW_ROUND_ADJUSTED_PREMONEY_FDS_LABEL,
  NEW_ROUND_ADJUSTED_PREMONEY_FDS_TOOLTIP,
  NEW_ROUND_CALCULATIONS_HEADER,
  NEW_ROUND_CURRENT_SHARES_OUTSTANDING_LABEL,
  NEW_ROUND_CURRENT_SHARES_OUTSTANDING_TOOLTIP,
  NEW_ROUND_DILUTION_LABEL,
  NEW_ROUND_DILUTION_TOOLTIP,
  NEW_ROUND_INCREMENTAL_OPTION_POOL_SHARES_LABEL,
  NEW_ROUND_ISSUE_PRICE_LABEL,
  NEW_ROUND_POOL_REFRESH_FACTOR_LABEL,
  NEW_ROUND_POST_MONEY_FDS_LABEL,
  NEW_ROUND_POST_MONEY_VALUATION_LABEL,
  NEW_ROUND_PREMONEY_FDS_LABEL,
  NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_LABEL,
  NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_TOOLTIP,
  NEW_ROUND_SHARES_ISSUED_LABEL,
  NEW_ROUND_SHARES_ISSUED_TOOLTIP,
  OPR_DECIMAL_PLACES,
  POOL_REFRESH_MODE_PRE,
} from 'common/constants/cap-table';
import { capTableCurrencyFormat, capTableTwoDecimalPercentFormat, costBasisCurrencyFormat } from 'common/formats/formats';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import theme from 'theme';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { getIssuePriceTooltip, getPostFdsTooltip, getPrfTooltip } from '../NewRoundModeler.utils';
import { blankCell, currencyCell, labelCell, numberCell, percentCell, sectionHeaderRow } from './sheetCellHelpers';
import { NrmParserProps, NrmTableData } from './types';

const successDark = { color: (theme.palette.success as { dark: string }).dark };

interface CalcRow {
  label: string;
  tooltip?: string;
  cell: ICell;
  bold?: boolean;
  valueStyle?: object;
}

const buildRows = ({ tableData }: NrmParserProps): CalcRow[] => {
  const { cso, puo, computed, poolRefreshMode } = tableData;

  if (!computed) {
    return [
      { label: NEW_ROUND_CURRENT_SHARES_OUTSTANDING_LABEL, tooltip: NEW_ROUND_CURRENT_SHARES_OUTSTANDING_TOOLTIP, cell: numberCell(cso), valueStyle: successDark },
      { label: NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_LABEL, tooltip: NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_TOOLTIP, cell: numberCell(puo), valueStyle: successDark },
      { label: NEW_ROUND_PREMONEY_FDS_LABEL, cell: numberCell(cso + puo) },
      { label: NEW_ROUND_ISSUE_PRICE_LABEL, cell: blankCell(), bold: true },
      { label: NEW_ROUND_SHARES_ISSUED_LABEL, tooltip: NEW_ROUND_SHARES_ISSUED_TOOLTIP, cell: blankCell(), bold: true },
      { label: NEW_ROUND_POST_MONEY_FDS_LABEL, cell: blankCell() },
      { label: NEW_ROUND_POST_MONEY_VALUATION_LABEL, cell: blankCell() },
      { label: NEW_ROUND_DILUTION_LABEL, tooltip: NEW_ROUND_DILUTION_TOOLTIP, cell: blankCell() },
    ];
  }

  const poolRefreshRows: CalcRow[] = [
    {
      label: NEW_ROUND_POOL_REFRESH_FACTOR_LABEL,
      tooltip: getPrfTooltip(poolRefreshMode),
      cell: numberCell(computed.poolRefreshFactor, OPR_DECIMAL_PLACES),
    },
    {
      label: NEW_ROUND_INCREMENTAL_OPTION_POOL_SHARES_LABEL,
      cell: numberCell(Math.round(computed.incrementalOptionPoolShares)),
    },
  ];

  const issuePriceRows: CalcRow[] = [
    {
      label: NEW_ROUND_ISSUE_PRICE_LABEL,
      tooltip: getIssuePriceTooltip(poolRefreshMode),
      cell: currencyCell(computed.newRoundIssuePrice, capTableCurrencyFormat),
      bold: true,
    },
    {
      label: NEW_ROUND_SHARES_ISSUED_LABEL,
      tooltip: NEW_ROUND_SHARES_ISSUED_TOOLTIP,
      cell: numberCell(Math.round(computed.newRoundSharesIssued)),
      bold: true,
    },
  ];

  const rows: CalcRow[] = [
    { label: NEW_ROUND_CURRENT_SHARES_OUTSTANDING_LABEL, tooltip: NEW_ROUND_CURRENT_SHARES_OUTSTANDING_TOOLTIP, cell: numberCell(cso), valueStyle: successDark },
    { label: NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_LABEL, tooltip: NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_TOOLTIP, cell: numberCell(puo), valueStyle: successDark },
    { label: NEW_ROUND_PREMONEY_FDS_LABEL, cell: numberCell(cso + puo) },
  ];

  if (poolRefreshMode === POOL_REFRESH_MODE_PRE) {
    rows.push(...poolRefreshRows);
    rows.push({
      label: NEW_ROUND_ADJUSTED_PREMONEY_FDS_LABEL,
      tooltip: NEW_ROUND_ADJUSTED_PREMONEY_FDS_TOOLTIP,
      cell: numberCell(Math.round(computed.adjustedPremoneyFullyDilutedShares)),
    });
    rows.push(...issuePriceRows);
  } else {
    rows.push(...issuePriceRows);
    rows.push(...poolRefreshRows);
  }

  rows.push({
    label: NEW_ROUND_POST_MONEY_FDS_LABEL,
    tooltip: getPostFdsTooltip(poolRefreshMode),
    cell: numberCell(Math.round(computed.postMoneyFullyDilutedShares)),
  });
  rows.push({ label: NEW_ROUND_POST_MONEY_VALUATION_LABEL, cell: currencyCell(computed.postMoneyValuation, costBasisCurrencyFormat) });
  rows.push({
    label: NEW_ROUND_DILUTION_LABEL,
    tooltip: NEW_ROUND_DILUTION_TOOLTIP,
    cell: percentCell(computed.dilutionToExistingHolders, capTableTwoDecimalPercentFormat),
  });

  return rows;
};

const calculationsParser = (params: NrmParserProps) => {
  const rows = buildRows(params);
  const alphabet = alphabetGenerator([], 2);
  const cells: Record<string, unknown> = { ...sectionHeaderRow(NEW_ROUND_CALCULATIONS_HEADER) };
  rows.forEach((row, rowIndex) => {
    // +2, not +1: row 1 is the header baked in above.
    const rowNumber = rowIndex + 2;
    cells[`${alphabet[0]}${rowNumber}`] = labelCell(row.label, row.tooltip);
    cells[`${alphabet[1]}${rowNumber}`] = {
      key: `${alphabet[1]}${rowNumber}`,
      ...row.cell,
      style: { ...row.cell.style, ...(row.bold ? { fontWeight: 700 } : {}), ...row.valueStyle },
    };
  });
  return cells;
};

// Row count varies with poolRefreshMode and whether `computed` exists yet.
const buildCalculationsRowConfig = (tableData: NrmTableData): object[] => {
  const rowCount = buildRows({ tableData }).length + 1;
  return Array.from({ length: rowCount }, () => ({}));
};

const createCalculationsSheetConfig = (tableData: NrmTableData) =>
  new SpreadsheetConfig<object, object, object, object>({
    name: 'nrmCalculations',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parser: calculationsParser as any,
    reverseParser: undefined,
    afterCellChanged: (changes: any) => changes,
    rowConfig: buildCalculationsRowConfig(tableData),
    columns: [{}, {}],
    tableData,
    isReadOnly: true,
    showTitlesColumn: false,
    hasColTitle: false,
    showToolbar: false,
    displayRowIndicator: false,
    displayColumnIndicator: false,
    currencyFormatter: false,
    unitsFormatter: false,
    ignoreCellExpressionErrors: false,
    // SpreadsheetConfigConstructorParams extends ScalarSpreadsheetConfigDataInterface which
    // requires internally-computed fields (evaluate, applyConditions, reverseParse, etc.)
    // that the constructor populates itself — not caller inputs.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);

export default createCalculationsSheetConfig;
